import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AutoFixHigh as AutoIcon
} from '@mui/icons-material';
import {
    CoreTopicDTO,
    createCoreTopic,
    updateCoreTopic,
    deleteCoreTopic
} from '../services/adminCourseService';
import adminApi from '../services/adminApi';
import ReviewChangesDialog, { TopicSuggestion } from './LLMTopicAssistant/ReviewChangesDialog';

interface SubtopicsManagerProps {
    subtopics: CoreTopicDTO[];
    topicId: number;
    subjectId: number;
    parentId: number; // Current topic ID is parent to these children
    onRefresh: () => void;
}

const SubtopicsManager: React.FC<SubtopicsManagerProps> = ({ subtopics, topicId, subjectId, parentId, onRefresh }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTopic, setEditingTopic] = useState<Partial<CoreTopicDTO> | null>(null);

    // AI Auto-Suggest state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [aiSuggestions, setAiSuggestions] = useState<TopicSuggestion[]>([]);
    const [applyingSuggestions, setApplyingSuggestions] = useState(false);
    const [promptPreview, setPromptPreview] = useState<string | null>(null);
    const [promptConfirmOpen, setPromptConfirmOpen] = useState(false);
    const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);

    const handleOpenCreate = () => {
        setEditingTopic({ title: '', description: '', parentTopicId: parentId });
        setOpenDialog(true);
    };

    const handleOpenEdit = (topic: CoreTopicDTO) => {
        setEditingTopic({ ...topic });
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!editingTopic || !editingTopic.title) return;

        try {
            if (editingTopic.id) {
                await updateCoreTopic(editingTopic.id, {
                    title: editingTopic.title,
                    description: editingTopic.description,
                    parentTopicId: editingTopic.parentTopicId || undefined,
                    displayOrder: editingTopic.sortOrder
                });
            } else {
                await createCoreTopic(subjectId, {
                    title: editingTopic.title,
                    description: editingTopic.description,
                    parentTopicId: parentId,
                    displayOrder: subtopics.length + 1
                });
            }
            setOpenDialog(false);
            onRefresh();
        } catch (error) {
            console.error('Failed to save subtopic', error);
            alert('Failed to save subtopic');
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this subtopic?')) {
            try {
                await deleteCoreTopic(id);
                onRefresh();
            } catch (error) {
                console.error('Failed to delete subtopic', error);
                alert('Failed to delete subtopic: ' + (error as any).response?.data?.message || 'Unknown error');
            }
        }
    };

    const requestBody = {
        query: '',
        parentTopicId: parentId,
        options: { includePrerequisites: false, suggestEdits: false, suggestDeletions: false },
    };

    const handleAIHelp = async () => {
        setAiError(null);
        setPromptPreviewLoading(true);
        try {
            const res = await adminApi.post<{ data: { prompt: string } }>(
                `/api/admin/graph/topics/subject/${subjectId}/llm-suggestions/preview`,
                requestBody,
                { timeout: 15000 }
            );
            setPromptPreview(res.data?.data?.prompt ?? '');
            setPromptConfirmOpen(true);
        } catch (err: any) {
            setAiError(err.response?.data?.message || err.message || 'Failed to load prompt preview');
        } finally {
            setPromptPreviewLoading(false);
        }
    };

    const handleConfirmPromptAndRun = async () => {
        setPromptConfirmOpen(false);
        setPromptPreview(null);
        setAiError(null);
        setAiLoading(true);
        try {
            const response = await adminApi.post(
                `/api/admin/graph/topics/subject/${subjectId}/llm-suggestions`,
                requestBody,
                { timeout: 120000 }
            );
            const responseData = response.data?.data;
            const suggestions = responseData?.suggestions || [];
            const mapped: TopicSuggestion[] = suggestions.map((s: any) => ({
                id: s.id,
                type: s.type,
                title: s.title,
                description: s.description,
                parentTopicId: s.parentTopicId,
                parentTopicTitle: s.parentTopicTitle,
                prerequisites: s.prerequisites?.map((p: any) => ({ id: p.id, title: p.title })) || [],
                displayOrder: s.displayOrder,
                originalData: s.originalTopicId ? { id: s.originalTopicId } : undefined,
                changes: s.changes ? {
                    title: s.changes.title ? { old: s.changes.title.old, new: s.changes.title.new_ || (s.changes.title as any).new } : undefined,
                    description: s.changes.description ? { old: s.changes.description.old, new: s.changes.description.new_ || (s.changes.description as any).new } : undefined,
                    parentTopicId: s.changes.parentTopicId ? { old: s.changes.parentTopicId?.old, new: s.changes.parentTopicId?.new_ || (s.changes.parentTopicId as any).new } : undefined,
                } : undefined,
                reason: s.reason,
            }));
            setAiSuggestions(mapped);
            setReviewDialogOpen(true);
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Failed to get AI suggestions';
            setAiError(message);
        } finally {
            setAiLoading(false);
        }
    };

    const handleApplyAISuggestions = async (acceptedSuggestions: TopicSuggestion[]) => {
        setApplyingSuggestions(true);
        setAiError(null);
        try {
            const adds = acceptedSuggestions.filter(s => s.type === 'add');
            const createdTopicMap = new Map<string, number>();
            let displayOrder = (subtopics?.length ?? 0) + 1;
            for (const suggestion of adds) {
                const response = await adminApi.post(`/api/admin/graph/topics/subject/${subjectId}`, {
                    title: suggestion.title,
                    description: suggestion.description,
                    parentTopicId: parentId,
                    displayOrder: displayOrder++,
                });
                const createdTopicId = response.data?.data?.id;
                if (createdTopicId) createdTopicMap.set(suggestion.id, createdTopicId);
            }
            for (const suggestion of adds) {
                if (suggestion.prerequisites?.length) {
                    const sourceTopicId = createdTopicMap.get(suggestion.id);
                    if (!sourceTopicId) continue;
                    for (const prereq of suggestion.prerequisites) {
                        const prereqTopicId = createdTopicMap.get(prereq.id.toString()) ?? Number(prereq.id);
                        if (Number.isNaN(Number(prereqTopicId))) continue;
                        try {
                            await adminApi.post(`/api/admin/graph/topics/${sourceTopicId}/prerequisites`, {
                                prerequisiteTopicId: prereqTopicId,
                                strength: 'REQUIRED',
                                isRequired: true,
                            });
                        } catch {
                            // skip failed prerequisite
                        }
                    }
                }
            }
            setReviewDialogOpen(false);
            onRefresh();
        } catch (err: any) {
            setAiError(err.response?.data?.message || err.message || 'Failed to apply suggestions');
        } finally {
            setApplyingSuggestions(false);
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                    Subtopics
                </Typography>
                <Box>
                    <Button
                        startIcon={(aiLoading || promptPreviewLoading) ? <CircularProgress size={16} /> : <AutoIcon />}
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={handleAIHelp}
                        disabled={aiLoading || promptPreviewLoading}
                    >
                        {promptPreviewLoading ? 'Loading…' : aiLoading ? 'Suggesting…' : 'AI Auto-Suggest'}
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        size="small"
                        onClick={handleOpenCreate}
                    >
                        Add Subtopic
                    </Button>
                </Box>
            </Box>

            {aiError && (
                <Alert severity="error" onClose={() => setAiError(null)} sx={{ mb: 2 }}>
                    {aiError}
                </Alert>
            )}
            {subtopics.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 4, textAlign: 'center' }}>
                    No subtopics defined. Add one to break down this topic further.
                </Typography>
            ) : (
                <List>
                    {subtopics.map((topic) => (
                        <Card key={topic.id} variant="outlined" sx={{ mb: 1 }}>
                            <ListItem>
                                <ListItemText
                                    primary={topic.title}
                                    secondary={topic.description || 'No description'}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEdit(topic)} sx={{ mr: 1 }}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(topic.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </Card>
                    ))}
                </List>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingTopic?.id ? 'Edit Subtopic' : 'New Subtopic'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Topic Title"
                        fullWidth
                        value={editingTopic?.title || ''}
                        onChange={(e) => setEditingTopic({ ...editingTopic, title: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={editingTopic?.description || ''}
                        onChange={(e) => setEditingTopic({ ...editingTopic, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={promptConfirmOpen} onClose={() => { setPromptConfirmOpen(false); setPromptPreview(null); }} maxWidth="md" fullWidth>
                <DialogTitle>Confirm prompt</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The following prompt will be sent to the AI. Click &quot;Confirm &amp; Run&quot; to generate subtopic suggestions.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        maxRows={20}
                        value={promptPreview ?? ''}
                        InputProps={{ readOnly: true }}
                        sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPromptConfirmOpen(false); setPromptPreview(null); }}>Back</Button>
                    <Button variant="contained" onClick={handleConfirmPromptAndRun} startIcon={<AutoIcon />}>Confirm &amp; Run</Button>
                </DialogActions>
            </Dialog>

            <ReviewChangesDialog
                open={reviewDialogOpen}
                onClose={() => !applyingSuggestions && setReviewDialogOpen(false)}
                onApply={handleApplyAISuggestions}
                suggestions={aiSuggestions}
                existingTopics={subtopics || []}
                applying={applyingSuggestions}
            />
        </Box>
    );
};

export default SubtopicsManager;
